import * as React from "react";
import { useMemo } from "react";
import { makeVisualization, retrieveChartData } from "views/components/aggregationbuilder/AggregationBuilder";
import PlotLegend from "views/components/visualizations/PlotLegend";
import useChartData from "views/components/visualizations/useChartData";
import useMapKeys from "views/components/visualizations/useMapKeys";
import { keySeparator, humanSeparator } from "views/Constants";
import usePieChartDataSettingsWithCustomUnits from "views/components/visualizations/hooks/usePieChartDataSettingsWithCustomUnits";
import usePlotOnClickPopover from "views/components/visualizations/hooks/usePlotOnClickPopover";
import pieOnClickPopover from "views/components/visualizations/pie/pieOnClickPopover";
import OverflowingComponentsContextProvider from "views/components/contexts/OverflowingComponentsContextProvider";
import GenericPlot from "../GenericPlot";
const maxItemsPerRow = 4;
const _verticalDimensions = (idx, total) => {
  const rows = Math.ceil(total / maxItemsPerRow);
  const position = Math.floor(idx / maxItemsPerRow);
  const sliceSize = 1 / rows;
  const spacer = sliceSize * 0.1;
  return [sliceSize * position + spacer, sliceSize * (position + 1) - spacer];
};
const _horizontalDimensions = (idx, total) => {
  const position = idx % maxItemsPerRow;
  const sliceSize = 1 / Math.min(total, maxItemsPerRow);
  const spacer = sliceSize * 0.1;
  return [sliceSize * position + spacer, sliceSize * (position + 1) - spacer];
};
const _generateSeries = (mapKeys, getPieChartDataSettingsWithCustomUnits) => ({ type, name, labels, values, idx, total, originalName, config, fullPath }) => {
  var _a, _b;
  const rowPivots = (_b = (_a = config == null ? void 0 : config.rowPivots) == null ? void 0 : _a.flatMap((pivot) => pivot.fields)) != null ? _b : [];
  const extendedSettings = getPieChartDataSettingsWithCustomUnits({ values, name, fullPath });
  return {
    type,
    name,
    hole: 0.4,
    labels: labels.map(
      (label) => label.split(keySeparator).map((l, i) => mapKeys(l, rowPivots[i])).join(humanSeparator)
    ),
    originalLabels: labels,
    values,
    automargin: true,
    textposition: "inside",
    domain: {
      x: _horizontalDimensions(idx, total),
      y: _verticalDimensions(idx, total)
    },
    originalName,
    ...extendedSettings
  };
};
const setChartColor = (chart, colorMap) => {
  const colors = chart.originalLabels.map((label) => colorMap.get(label));
  return { marker: { colors } };
};
const labelMapper = (data) => [
  ...new Set(data.flatMap(({ labels, originalLabels }) => originalLabels != null ? originalLabels : labels))
];
const rowPivotsToFields = (config) => {
  var _a;
  return (_a = config == null ? void 0 : config.rowPivots) == null ? void 0 : _a.flatMap((pivot) => pivot.fields);
};
const PieVisualization = makeVisualization(({ config, data, height, width }) => {
  const rows = useMemo(() => retrieveChartData(data), [data]);
  const mapKeys = useMapKeys();
  const getPieChartDataSettingsWithCustomUnits = usePieChartDataSettingsWithCustomUnits({ config });
  const transformedData = useChartData(rows, {
    widgetConfig: config,
    chartType: "pie",
    generator: _generateSeries(mapKeys, getPieChartDataSettingsWithCustomUnits)
  });
  const { popover, initializeGraphDivRef, onChartClick } = usePlotOnClickPopover({ ...pieOnClickPopover, config });
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    PlotLegend,
    {
      config,
      chartData: transformedData,
      labelMapper,
      labelFields: rowPivotsToFields,
      neverHide: true,
      height,
      width
    },
    /* @__PURE__ */ React.createElement(
      GenericPlot,
      {
        chartData: transformedData,
        setChartColor,
        onInitialized: initializeGraphDivRef,
        onClickMarker: onChartClick
      }
    )
  ), /* @__PURE__ */ React.createElement(OverflowingComponentsContextProvider, null, popover));
}, "pie");
PieVisualization.displayName = "PieVisualization";
export default PieVisualization;
